import { useSessionKey } from "@auth/sessionStore";
import {
  CalendarIcon,
  ChevronRightIcon,
  type CheckState,
  ClockIcon,
  cn,
  formatClockTime,
  StateCheckbox,
} from "@scribenote/brian-design-system";
import type { Task } from "@tasks/tasksClient";
import { useCompleteTask } from "@tasks/tasksQueries";
import { todoDetail } from "@todos/todoFields";
import { useClinicRoles } from "@users/rolesQueries";
import { useClinicUsers } from "@users/userQueries";
import { formatMonthDay } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import { useId, useState } from "react";

const DAY_MS = 86_400_000;

export const isUrgentTodo = (task: Task): boolean => task.severity === "urgent";

export const todoCheckState = (status: string): CheckState =>
  status === "open" ? "open" : status === "cancelled" ? "declined" : "done";

const isOverdue = (task: Task, now: number): boolean =>
  task.dueAt !== null && Date.parse(task.dueAt) < now;

const useOwner = (task: Task): string | null => {
  const sessionKey = useSessionKey();
  const { data: users } = useClinicUsers(sessionKey);
  const { data: roles } = useClinicRoles(sessionKey);
  if (task.assigneeUserId) {
    const name = users?.find((u) => u.id === task.assigneeUserId)?.displayName;
    return name?.trim().split(/\s+/)[0] || null;
  }
  if (task.assignedRoleId)
    return roles?.find((r) => r.id === task.assignedRoleId)?.name ?? null;
  return null;
};

const Due = ({ at, now }: { at: string; now: number }) => {
  const date = new Date(at);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const days = Math.floor((date.getTime() - today.getTime()) / DAY_MS);
  const sameDay = days === 0;
  const text = sameDay
    ? formatClockTime(date)
    : days > 0 && days < 7
      ? date.toLocaleDateString([], { weekday: "long" })
      : formatMonthDay(at);
  const Glyph = sameDay ? ClockIcon : CalendarIcon;
  return (
    <time dateTime={date.toISOString()} className="inline-flex items-center">
      <Glyph className="mr-[0.35em]" />
      {text}
    </time>
  );
};

export const TodoRow = ({
  task,
  className,
}: {
  task: Task;
  className?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  const now = useNow();
  const complete = useCompleteTask();
  const owner = useOwner(task);
  const detail = todoDetail(task);
  const urgent = isUrgentTodo(task);
  const closed = task.status !== "open";
  const overdue = !closed && isOverdue(task, now);
  const closing = complete.isPending || complete.isSuccess;
  const hasAside = Boolean(owner || task.dueAt);
  const title = (
    <>
      {urgent && <span className="sr-only">Urgent: </span>}
      {task.title}
    </>
  );

  return (
    <li
      className={cn(
        "-mx-2.5 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2 rounded-sheet border-b border-dotted border-rule px-2.5 py-3",
        hasAside && "grid-cols-[auto_minmax(0,1fr)_auto]",
        urgent && "border-transparent bg-tint-declined",
        className,
      )}
    >
      <StateCheckbox
        state={closing ? "done" : todoCheckState(task.status)}
        label={`Done: ${task.title}`}
        disabled={closing || closed}
        onToggle={(next) => {
          if (next === "done") complete.mutate(task);
        }}
      />
      <div className="min-w-0 [overflow-wrap:anywhere]">
        {detail ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => setExpanded((value) => !value)}
            className="flex min-h-7 w-full cursor-pointer items-center justify-between gap-2 rounded-paper text-left text-[1.0625rem] font-semibold leading-[1.3] text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <span className="min-w-0">{title}</span>
            <ChevronRightIcon
              className={cn(
                "shrink-0 text-annotation",
                expanded && "rotate-90",
              )}
            />
          </button>
        ) : (
          <p className="text-[1.0625rem] font-semibold leading-[1.3] text-ink">
            {title}
          </p>
        )}
      </div>
      {hasAside && (
        <div className="flex flex-col items-end gap-1">
          {owner && (
            <span className="rounded-tag bg-wall px-2 py-[3px] font-mono text-fine-print uppercase tracking-[0.08em] text-ink-body">
              {owner}
            </span>
          )}
          {task.dueAt && (
            <span
              className={cn(
                "text-mono-time text-xs",
                urgent || overdue ? "text-ink-declined" : "text-annotation",
              )}
            >
              <Due at={task.dueAt} now={now} />
            </span>
          )}
        </div>
      )}
      {detail && (
        <p
          id={detailId}
          hidden={!expanded}
          className="col-start-2 min-w-0 whitespace-pre-wrap text-sm text-annotation [overflow-wrap:anywhere]"
        >
          {detail}
        </p>
      )}
    </li>
  );
};
